import { isExternalUrl } from "../navigation/navigation-utils.js";

function decorateEssentialLinkLabel(link) {
  if (!link) return link;

  const href = link.getAttribute("href");

  if (!isExternalUrl(href)) return link;

  if (
    link.classList.contains("is-external") ||
    link.querySelector(".sz-link-external-icon")
  ) {
    return link;
  }

  const label = link.querySelector(".sz-icon-grid__label");

  if (!label) return link;

  link.classList.add("is-external");

  const icon = document.createElement("span");
  icon.className = "sz-link-external-icon sz-icon-grid__external-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML = `<i class="bi bi-box-arrow-up-right"></i>`;

  label.appendChild(icon);

  const opensInNewWindow = link.getAttribute("target") === "_blank";
  const suffix = opensInNewWindow
    ? "external link, opens in a new window"
    : "external link";

  const ariaLabel = link.getAttribute("aria-label");

  if (ariaLabel) {
    const normalised = ariaLabel.toLowerCase();

    if (!normalised.includes("external link")) {
      link.setAttribute("aria-label", `${ariaLabel}, ${suffix}`);
    }
  } else {
    const srText = document.createElement("span");
    srText.className = "visually-hidden";
    srText.textContent = ` ${suffix}`;
    label.appendChild(srText);
  }

  return link;
}

export function initEssentialLinks(root = document) {
  console.log("INIT ESSENTIAL LINKS RUNNING");

  const containers = root.querySelectorAll('[data-module="essential-links"]');

  const source = document.getElementById("sz-essential-links-source");

  if (!source) {
    console.warn("❌ No source found");
    return;
  }

  const allItems = Array.from(
    source.querySelectorAll(".sz-essential-link-item"),
  );

  console.log("ITEMS FOUND:", allItems.length);

  if (!allItems.length) {
    console.warn("❌ No essential link items found");
    return;
  }

  containers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    const isModal = container.dataset.mode === "modal";

    if (container.dataset.initialised && !isModal) return;

    container.dataset.initialised = "true";

    // ✅ FILTER FOR MODAL
    const items = isModal
      ? allItems.filter((item) => item.dataset.tab === "Essential Links")
      : allItems;

    console.log("USING ITEMS:", items.length, isModal ? "(modal)" : "(full)");

    if (!items.length) return;

    // ✅ MODAL RENDER
    if (isModal) {
      renderModalList(container, items);
      injectInlineSvg(container);
      return;
    }

    // ✅ FULL RENDER (homepage)
    const groups = groupItems(items);

    console.log("GROUPS:", groups);

    const desktop = buildDesktopTabs(groups);
    const mobile = buildMobileAccordion(groups);

    container.innerHTML = "";
    container.appendChild(desktop);
    container.appendChild(mobile);

    bindTabInteractions(desktop);
    bindAccordionInteractions(mobile);
    injectInlineSvg(container);
  });
}

function groupItems(items) {
  const groups = {};

  items.forEach((item) => {
    const tab = item.dataset.tab || "Other";
    const theme = item.dataset.theme || "yellow";

    if (!groups[tab]) {
      groups[tab] = {
        theme,
        items: [],
      };
    }

    groups[tab].items.push(item);
  });

  return groups;
}

function buildDesktopTabs(groups) {
  const wrapper = document.createElement("div");
  wrapper.className = "sz-essential-links__desktop";

  const tabsList = document.createElement("div");
  tabsList.className = "sz-essential-links__tabs";
  tabsList.setAttribute("role", "tablist");

  const panelsWrapper = document.createElement("div");

  Object.entries(groups).forEach(([tab, group], index) => {
    const tabId = `tab-${index}`;
    const panelId = `panel-${index}`;

    // TAB BUTTON
    const btn = document.createElement("button");
    btn.className = `
      sz-essential-links__tab
      sz-essential-links__tab--${group.theme}
      ${index === 0 ? "is-active" : ""}
    `;

    btn.textContent = tab;
    btn.dataset.tab = tab;
    btn.id = tabId;

    btn.setAttribute("role", "tab");
    btn.setAttribute("aria-selected", index === 0 ? "true" : "false");
    btn.setAttribute("aria-controls", panelId);

    tabsList.appendChild(btn);

    // PANEL
    const panel = document.createElement("div");
    panel.className = `
      sz-essential-links__panel
      sz-essential-links__panel--${group.theme}
      ${index === 0 ? "is-active" : ""}
    `;

    panel.id = panelId;
    panel.dataset.panel = tab;

    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", tabId);

    panel.appendChild(buildList(group.items));

    panelsWrapper.appendChild(panel);
  });

  wrapper.appendChild(tabsList);
  wrapper.appendChild(panelsWrapper);

  return wrapper;
}

function buildMobileAccordion(groups) {
  const wrapper = document.createElement("div");
  wrapper.className = "sz-essential-links__mobile";

  Object.entries(groups).forEach(([tab, group], index) => {
    const btnId = `acc-btn-${index}`;
    const panelId = `acc-panel-${index}`;

    const section = document.createElement("div");
    section.className = `sz-essential-links__accordion-item sz-essential-links__accordion-item--${group.theme}`;

    // BUTTON
    const button = document.createElement("button");
    button.className = "sz-essential-links__accordion-toggle";
    button.textContent = tab;

    button.id = btnId;
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-controls", panelId);

    // PANEL
    const panel = document.createElement("div");
    panel.className = "sz-essential-links__accordion-panel";
    panel.id = panelId;

    panel.setAttribute("role", "region");
    panel.setAttribute("aria-labelledby", btnId);
    panel.hidden = true;

    panel.appendChild(buildList(group.items));

    section.appendChild(button);
    section.appendChild(panel);
    wrapper.appendChild(section);
  });

  return wrapper;
}

function buildList(items) {
  const list = document.createElement("ul");
  list.className = "sz-icon-grid";

  items.forEach((item) => {
    const li = document.createElement("li");
    li.className = "sz-icon-grid__item";

    const link = item.querySelector("a");
    if (link) {
      const clone = link.cloneNode(true);

      decorateEssentialLinkLabel(clone);

      li.appendChild(clone);
    }

    if (item.dataset.customSvg) {
      li.dataset.customSvg = item.dataset.customSvg;
    }

    list.appendChild(li);
  });

  return list;
}

function bindTabInteractions(root) {
  const tabs = root.querySelectorAll('[role="tab"]');
  const panels = root.querySelectorAll('[role="tabpanel"]');

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => activateTab(index));

    tab.addEventListener("keydown", (e) => {
      let next = index;

      if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
      else if (e.key === "ArrowLeft")
        next = (index - 1 + tabs.length) % tabs.length;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = tabs.length - 1;
      else return;

      tabs[next].focus();
      e.preventDefault();
    });
  });

  function activateTab(index) {
    tabs.forEach((t, i) => {
      t.classList.toggle("is-active", i === index);
      t.setAttribute("aria-selected", i === index ? "true" : "false");
    });

    panels.forEach((p, i) => {
      p.classList.toggle("is-active", i === index);
    });
  }
}

function bindAccordionInteractions(root) {
  root.addEventListener("click", (e) => {
    const btn = e.target.closest(".sz-essential-links__accordion-toggle");
    if (!btn) return;

    const expanded = btn.getAttribute("aria-expanded") === "true";
    const panel = document.getElementById(btn.getAttribute("aria-controls"));

    btn.setAttribute("aria-expanded", String(!expanded));
    if (panel) panel.hidden = expanded;
  });
}

function injectInlineSvg(root) {
  const items = root.querySelectorAll("[data-custom-svg]");

  items.forEach((item) => {
    const encoded = item.dataset.customSvg;
    const holder = item.querySelector(".inline-svg");

    if (!encoded || !holder) return;

    const textarea = document.createElement("textarea");
    textarea.innerHTML = encoded;
    const decoded = textarea.value;
    holder.innerHTML = decoded;

    const svg = holder.querySelector("svg");
    if (!svg) return;

    svg.querySelectorAll("style").forEach((s) => s.remove());
    svg.querySelectorAll("[fill]").forEach((el) => el.removeAttribute("fill"));

    svg.setAttribute("fill", "currentColor");
    svg.removeAttribute("width");
    svg.removeAttribute("height");

    svg.style.width = "100%";
    svg.style.height = "100%";

    svg.setAttribute("aria-hidden", "true");
  });
}

function renderModalList(container, items) {
  const filtered = Array.from(items).filter(
    (item) => item.dataset.tab === "Essential Links",
  );

  if (!filtered.length) return;

  // 👉 take theme from first item (consistent with your tab logic)
  const theme = filtered[0].dataset.theme || "yellow";

  const wrapper = document.createElement("div");
  wrapper.className = `
    sz-essential-links__modal-list
    sz-essential-links__modal-list--${theme}
  `;

  const list = buildList(filtered);

  wrapper.appendChild(list);

  container.innerHTML = "";
  container.appendChild(wrapper);
}
